import * as Sentry from "@sentry/react";
import { useMutation } from "@tanstack/react-query";
import { useCallback } from "react";
import { useIntl } from "react-intl";

import {
  PurchaseStatus,
  type Callbacks,
  type PaymentResult,
  type TripPaymentParams,
} from "@/app/payments/types";
import { platformApi } from "@/app/platform";
import { showSnackbar } from "@/contexts/SnackbarContext";
import { updatePersonalData } from "@/utils/bookings";

type Params = {
  onSuccess: (result: PaymentResult) => void;
  onPaymentSuccess: Callbacks["onSuccess"];
  onPaymentFailure: Callbacks["onFailure"];
};

export const useTripBooking = ({
  onSuccess,
  onPaymentSuccess,
  onPaymentFailure,
}: Params) => {
  const intl = useIntl();

  const handleError = useCallback(
    (error: unknown) => {
      console.error("Error in booking or payment process:", error);
      Sentry.captureException(error);
      showSnackbar({
        type: "error",
        content: intl.formatMessage({
          defaultMessage: "Booking failed, please try again later.",
          description:
            "Error message shown when the trip booking request fails",
        }),
      });
    },
    [intl],
  );

  const qiPayment = useMutation({
    mutationFn: async (params: TripPaymentParams) => {
      const wasSuccess = await updatePersonalData();
      if (!wasSuccess) {
        return {
          orderId: "",
          status: PurchaseStatus.CANCELLED,
        };
      }

      return platformApi.getPaymentProvider("standard").initiatePayment({
        ...params,
        serviceType: "trip",
        onSuccess: onPaymentSuccess,
        onFailure: onPaymentFailure,
      });
    },
    onSuccess,
    onError: handleError,
  });

  return {
    qiPayment,
  };
};
